import React from 'react';

import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import { FormProvider } from 'react-hook-form';

import { AppButton } from '../../components/AppButton';
import { MAIN_CONTAINER_ID } from '../../constants';
import { SuccessDialog } from './components';
import { WhereStep } from './components/WhereStep';
import { WhoStep } from './components/WhoStep';
import { useInviteWorkersDialog } from './hooks';
import useStyles from './styles';

interface Props {
  /** Rendered above the tabs on the first step — Legend passes its company picker here */
  children?: React.ReactElement;
}

/**
 * Invite people into the company: new contacts by mobile or existing Vyce users, then the
 * modules they should land in. Two steps — who, then where — so a bulk selection is reviewed
 * before it is sent. Wrap it in UniversalInviteProvider.
 */
export const UniversalInviteDialog = ({ children }: Props) => {
  const classes = useStyles();
  const dialog = useInviteWorkersDialog();
  const { step, counts, summaryText, selection, targets } = dialog;
  const isWhoStep = step === 'who';

  return (
    <>
      <Dialog
        container={document.getElementById(MAIN_CONTAINER_ID)}
        classes={{ paper: classes.dialogPaper }}
        fullScreen={dialog.isMobile}
        maxWidth="md"
        fullWidth
        open={dialog.open}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            dialog.handleClose();
          }
        }}
      >
        <DialogTitle disableTypography className={classes.titleRoot}>
          <Typography variant="h6" className={classes.title}>
            {isWhoStep ? 'Invite team members' : 'Where would you like to add them?'}
          </Typography>
          <Typography variant="body2" color="textSecondary" className={classes.subtitle}>
            {isWhoStep
              ? 'Add existing Vyce users or invite someone new by mobile.'
              : 'People with complete details are added straight away; the rest get an invite to finish setting up.'}
          </Typography>
        </DialogTitle>

        <FormProvider {...dialog.methods}>
          <form onSubmit={event => event.preventDefault()}>
            <DialogContent className={classes.content}>
              {isWhoStep && children && <Box marginBottom={2}>{children}</Box>}

              {/* Step 1 stays mounted behind step 2 so Back is instant and loses nothing */}
              <Box hidden={!isWhoStep}>
                <WhoStep
                  tab={dialog.tab}
                  onTabChange={dialog.setTab}
                  newCount={counts.phones}
                  existingCount={counts.existing}
                  phones={selection.state.phones}
                  addPhoneToList={dialog.addPhoneToList}
                  handlePhoneFieldKeyDown={dialog.handlePhoneFieldKeyDown}
                  deleteContact={selection.removePhone}
                  contactsDisabled={dialog.contactsDisabled}
                  bulkPaste={dialog.bulkPaste}
                  companyId={dialog.companyId}
                  filter={dialog.filter}
                  onFilterChange={dialog.setFilter}
                  filterModules={dialog.filterModules}
                  resourceOptions={dialog.resourceOptions}
                  selection={selection}
                  noModulesAvailable={targets.noModulesAvailable}
                  limitError={dialog.limitError}
                />
              </Box>

              {!isWhoStep && <WhereStep selection={selection} targets={targets} />}
            </DialogContent>
          </form>
        </FormProvider>

        <DialogActions className={classes.actions}>
          <Typography className={classes.footerText}>
            {summaryText ? (
              <>
                <span className={classes.footerStrong}>{summaryText.total}</span> ready
                {summaryText.breakdown}
              </>
            ) : (
              'No one selected yet'
            )}
          </Typography>

          <Box display="flex" gridGap={8}>
            {isWhoStep ? (
              <>
                <AppButton size="large" variant="outlined" onClick={dialog.handleClose}>
                  Cancel
                </AppButton>
                <AppButton
                  size="large"
                  variant="contained"
                  color="primary"
                  disabled={!dialog.canGoNext}
                  onClick={dialog.goNext}
                >
                  Next
                </AppButton>
              </>
            ) : (
              <>
                <AppButton size="large" variant="outlined" onClick={dialog.goBack} disabled={dialog.loading}>
                  Back
                </AppButton>
                <AppButton
                  size="large"
                  variant="contained"
                  color="primary"
                  loading={dialog.loading}
                  loaderVariant="paper"
                  disabled={!targets.isValid}
                  onClick={dialog.invite}
                >
                  Invite {counts.total} {counts.total === 1 ? 'person' : 'people'}
                </AppButton>
              </>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <SuccessDialog
        open={dialog.isSuccessDialogOpen}
        closeDialog={dialog.closeSuccessDialog}
        message={dialog.successMessage}
        hideUserManagementLink={dialog.isLegend}
      />
    </>
  );
};
